import { useState, useCallback, useEffect, useRef } from 'react';

import fetchEndpoint from '../../../services/fetchEndpoint';

import {
  getIdentifiersList,
  getSecondUrl,
  getRemainingPages,
  chunkCommaSeparatedIds,
} from '../Charts/Common/MultipleRequestsHelpers';

import { userAppContext } from '../../../context/Context';

interface UseNetworkChartProps {
  chartInfo: any;
  initialChartData: { nodes: any[]; edges: any[] };
  keySearch: any;
  source: any;
  baseUrl: any;
  parent: any;
  pageSize: number;
  getNetworkData: any;
  unifyData: any;
  uniqueIdentViewsVariables: any;
  names: any;
  view: any;
}

function useNetworkChart({
  chartInfo,
  initialChartData,
  keySearch,
  source,
  baseUrl,
  parent,
  pageSize,
  getNetworkData,
  unifyData,
  uniqueIdentViewsVariables,
  names,
  view,
}: UseNetworkChartProps) {
  const fetchAll = pageSize === -1;
  const effectivePageSize = fetchAll ? 100 : pageSize;
  const [identReqParam, setIdentReqParam] = useState('');
  const [chartData, setChartData] = useState(initialChartData);

  const maxEdgeThicknessRef = useRef(1);
  const [isPayloadEmpty, setIsPayloadEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  const { authorization, setIsAuthenticated } = userAppContext() || {};

  const getIdentReqData = useCallback(async () => {
    setIsLoading(true);

    const orderByParam =
      chartInfo.variable === 'numPMIDsupportingAssociation'
        ? `${chartInfo.variable}_${source}`
        : chartInfo.variable;

    const identName = uniqueIdentViewsVariables[parent].identList[view];
    const firstUrl = `${baseUrl}?source=${source}&${keySearch}&page_number=${0}&page_size=${effectivePageSize}&order_by=${orderByParam}&order=desc`;
    const firstResponse = await fetchEndpoint(firstUrl, authorization, setIsAuthenticated);
    if (firstResponse === undefined) {
      setIsLoading(false);
      return;
    }
    const firstData = firstResponse.payload || [];
    const totalElements = firstResponse?.paging?.totalElements ?? firstData?.length ?? 0;
    setTotalResults(totalElements);

    if (firstData.length === 0) {
      setIsPayloadEmpty(true);
      setIsLoading(false);
      return;
    }
    setIsPayloadEmpty(false);

    let identifiers = getIdentifiersList(firstData, identName);
    if (fetchAll) {
      const totalPages = Math.ceil(totalElements / effectivePageSize);
      for (let currentPage = 1; currentPage < totalPages; currentPage += 1) {
        const pagedUrl = `${baseUrl}?source=${source}&${keySearch}&page_number=${currentPage}&page_size=${effectivePageSize}&order_by=${orderByParam}&order=desc`;
        const pagedResponse = await fetchEndpoint(pagedUrl, authorization, setIsAuthenticated);
        if (pagedResponse?.payload?.length) {
          identifiers = [...identifiers, ...getIdentifiersList(pagedResponse.payload, identName)];
        }
      }
    }

    setIdentReqParam(Array.from(new Set(identifiers)).join(','));
  }, [
    source,
    keySearch,
    chartInfo,
    pageSize,
    fetchAll,
    effectivePageSize,
    parent,
    view,
    baseUrl,
    authorization,
    setIsAuthenticated,
  ]);

  useEffect(() => {
    getIdentReqData();

    return () => {
      setChartData(initialChartData);
      setIdentReqParam('');
      setIsLoading(false);
    };
  }, [getIdentReqData]);

  useEffect(() => {
    if (!identReqParam) {
      return;
    }

    let cancelled = false;

    const identParam = uniqueIdentViewsVariables[parent].secondReqIdent[view];
    const idChunks =
      typeof identParam === 'string' ? chunkCommaSeparatedIds(identReqParam, 1800) : [identReqParam];

    const run = async () => {
      setIsLoading(true);
      setChartData({ nodes: [], edges: [] });
      maxEdgeThicknessRef.current = 1;

      const orderByParam =
        chartInfo.variable === 'numPMIDsupportingAssociation'
          ? `${chartInfo.variable}_${source}`
          : chartInfo.variable;

      const mappingVariables = uniqueIdentViewsVariables[parent].mappingIdents[view];
      const model = uniqueIdentViewsVariables[parent].model;

      let accumulated = { nodes: [] as any[], edges: [] as any[] };
      let globalPageIndex = 0;

      const setMaxEdgeThickness = (value: number) => {
        maxEdgeThicknessRef.current = value;
      };

      for (const chunk of idChunks) {
        let pageNum = 0;
        let hasMorePages = true;
        while (hasMorePages) {
          if (cancelled) {
            return;
          }

          const pagingInfo = {
            currentPageNumber: pageNum,
            pageSize: effectivePageSize,
            remainingPages: undefined,
            totalElements: undefined,
          };
          const url = getSecondUrl(baseUrl, source, keySearch, identParam, chunk, pagingInfo, orderByParam);

          let responseData;
          try {
            responseData = await fetchEndpoint(url, authorization, setIsAuthenticated);
          } catch {
            if (!cancelled) {
              setIsLoading(false);
            }
            return;
          }

          if (cancelled) {
            return;
          }
          if (responseData === undefined) {
            setIsLoading(false);
            return;
          }

          const data = responseData.payload;
          const currentPagingInfo = responseData.paging;

          if (!currentPagingInfo) {
            break;
          }

          const adjustedPagingInfo = {
            ...currentPagingInfo,
            currentPageNumber: globalPageIndex,
          };

          const transformedNetworkChartData = getNetworkData(
            data,
            mappingVariables,
            model,
            chartInfo.variable,
            adjustedPagingInfo,
            setMaxEdgeThickness,
            maxEdgeThicknessRef.current,
            names,
            parent,
          );

          accumulated = unifyData(accumulated, transformedNetworkChartData);
          setChartData(accumulated);

          globalPageIndex += 1;

          const remaining = getRemainingPages(
            pageNum + 1,
            currentPagingInfo.totalElements,
            currentPagingInfo.pageSize,
          );
          if (remaining === 0) {
            hasMorePages = false;
          } else {
            pageNum += 1;
          }
        }
      }

      if (!cancelled) {
        setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    identReqParam,
    chartInfo.variable,
    chartInfo,
    parent,
    view,
    baseUrl,
    source,
    keySearch,
    effectivePageSize,
    getNetworkData,
    unifyData,
    names,
    authorization,
    setIsAuthenticated,
  ]);

  return { chartData, isLoading, isPayloadEmpty, totalResults };
}

export default useNetworkChart;
